// apps/client/src/components/auth/MfaRecoveryPanel.tsx
//
// "Can't access your authenticator?" - the lost-device recovery screen from the
// founder-approved MFA mockup (screen 3). This is NOT the App Check throttle
// path (that reuses AppCheckRecoveryBanner). GCIP allows only one authenticator
// per account, so there is no self-service second code: recovery is a mediated
// reset, stated plainly here.
//
// COPY ACCURACY (corrected 2026-08-17). This panel previously read "Contact your
// administrator to reset your second factor. They can clear it in seconds",
// which promised a capability that does not exist: there is no workspace-admin
// reset surface, and the server-side reset callable
// (`services/admin-functions/src/mfa/resetUserMfa.ts`) is DORMANT pending the
// GCIP upgrade, with its tenant-admin basis switched OFF pending a founder
// ruling (`TENANT_ADMIN_RESET_ENABLED`). Until that ruling lands, resets are
// performed by Valutten, so the copy names Valutten and makes no promise about
// how fast it happens. If the tenant-admin basis is later enabled, this copy
// must be revisited in the SAME change, not afterwards: the whole point of this
// screen is that it describes the recovery a locked-out person will actually
// get.

import React from 'react';
import { AlertTriangle, Mail } from 'lucide-react';

interface MfaRecoveryPanelProps {
  /** Return to the sign-in code entry. */
  onBack: () => void;
}

const SUPPORT_EMAIL = 'support@valutten.com';
const SUPPORT_SUBJECT = 'Two-factor reset request';

const buildSupportMailto = (): string => {
  const body = [
    'Hi VALUTTEN team,',
    '',
    'I have lost access to my authenticator app and need my two-factor setup reset.',
    '',
    `Timestamp: ${new Date().toISOString()}`,
    '',
    'Thanks,',
  ].join('\n');
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(SUPPORT_SUBJECT)}&body=${encodeURIComponent(body)}`;
};

const STEPS: string[] = [
  'Email Valutten support from the address on your account, or ask your workspace admin to raise it for you.',
  'We confirm it is you before anything changes. Every reset is recorded in the audit log.',
  'Once it is cleared, you sign in with your password and enrol a new code on your new device.',
];

const MfaRecoveryPanel: React.FC<MfaRecoveryPanelProps> = ({ onBack }) => {
  return (
    <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 mb-4">
          <AlertTriangle size={30} className="text-gray-900" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Can&rsquo;t access your authenticator?</h2>
        <p className="text-gray-400 text-sm leading-relaxed">
          Lost or replaced your phone? Your two-factor setup can be reset. There is no self-service
          second code by design, so recovery is handled for you.
        </p>
      </div>

      <div
        className="bg-amber-900/40 border border-amber-500/50 text-amber-100 p-4 rounded-lg mb-5 flex items-start"
        role="status"
      >
        <AlertTriangle size={18} className="mr-3 flex-shrink-0 mt-0.5 text-amber-300" />
        <span className="text-sm leading-relaxed">
          <b className="text-white">Request a reset from Valutten.</b> We verify who you are, clear
          the second factor and log the change. You then sign in with your password and set up a
          fresh code.
        </span>
      </div>

      <ol className="mb-6 space-y-0">
        {STEPS.map((step, index) => (
          <li
            key={step}
            className="flex items-start gap-3 py-2.5 text-sm text-gray-300 leading-relaxed border-b border-gray-700/50 last:border-b-0"
          >
            <span className="flex-none w-6 h-6 rounded-full bg-emerald-500/15 text-emerald-400 grid place-items-center text-xs font-bold">
              {index + 1}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 bg-transparent border border-gray-600 text-gray-100 p-3 rounded-lg font-medium hover:border-emerald-500/60 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
        >
          Back to sign in
        </button>
        <a
          href={buildSupportMailto()}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white p-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <Mail size={16} />
          Contact support
        </a>
      </div>
    </div>
  );
};

export default MfaRecoveryPanel;
